import { useCallback, useEffect, useState } from "react";
import { ADSENSE_CLIENT } from "./AdSlot";
import { platformAllowsDeviceAccess, useConsent } from "../lib/consent";
import { useI18n } from "../i18n";
import Pic from "./pics";

// On in production builds whenever ads are configured. Set
// REACT_APP_ADBLOCK_WALL=off to disable it.
const ENABLED =
  !!ADSENSE_CLIENT && process.env.NODE_ENV === "production" && process.env.REACT_APP_ADBLOCK_WALL !== "off";

// Three checks: a "bait" element that blockers hide via cosmetic filters, the
// AdSense script from index.html (blocked or emptied by network filters, like
// Opera's and Brave's built-in blockers), and a request to it.
async function detectAdblock() {
  const bait = document.createElement("div");
  bait.className = "adsbox ad-banner textads banner-ads pub_300x250 adsbygoogle";
  bait.setAttribute("aria-hidden", "true");
  bait.style.cssText = "position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;";
  bait.innerHTML = "&nbsp;";
  document.body.appendChild(bait);
  await new Promise((r) => setTimeout(r, 150));
  const style = window.getComputedStyle(bait);
  const hidden = bait.offsetHeight === 0 || style.display === "none" || style.visibility === "hidden";
  bait.remove();
  if (hidden) return true;

  // The page's AdSense script sets adsbygoogle.loaded once it has run.
  if (document.readyState === "complete" && window.adsbygoogle && window.adsbygoogle.loaded !== true) {
    const script = document.querySelector('script[src*="adsbygoogle.js"]');
    if (script) return true;
  }

  // A blocked request fails; one that hangs counts as not blocked.
  const timeout = new Promise((resolve) => setTimeout(() => resolve(false), 4000));
  const probe = fetch("https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js", {
    method: "HEAD",
    mode: "no-cors",
    cache: "no-store",
  }).then(
    () => false,
    () => true
  );
  return Promise.race([probe, timeout]);
}

export default function AdblockWall() {
  const { t, tx } = useI18n();
  const [blocked, setBlocked] = useState(false);
  const [checking, setChecking] = useState(false);
  // Checking for an ad blocker reads from the visitor's browser, so (EU
  // ePrivacy rules) only do it for visitors who allowed that: in our cookie
  // banner, or in Google's consent message when that's the one asking.
  const { consent } = useConsent();
  const [allowed, setAllowed] = useState(false);
  useEffect(() => {
    if (!consent) return;
    if (!consent.platform) return setAllowed(!!consent.ads);
    let alive = true;
    platformAllowsDeviceAccess().then((ok) => alive && setAllowed(ok));
    return () => {
      alive = false;
    };
  }, [consent]);

  const check = useCallback(async () => {
    if (!ENABLED || !allowed) return;
    setChecking(true);
    const result = await detectAdblock();
    setBlocked(result);
    setChecking(false);
  }, [allowed]);

  useEffect(() => {
    check();
  }, [check]);

  if (!blocked) return null;

  return (
    <div className="adblock-wall" role="alertdialog" aria-modal="true" aria-labelledby="adblock-title">
      <div className="adblock-card">
        <div className="adblock-emoji">
          <Pic name="eyeOff" />
        </div>
        <h2 id="adblock-title">{t("Whoa, an ad blocker!")}</h2>
        <p>{t("PartyCade is free for you and your friends because of a few small ads in the menus. We never show ads during a round.")}</p>
        <ol className="adblock-steps">
          <li>{t("Click your ad blocker's icon in the browser toolbar.")}</li>
          <li>
            {tx('Choose <strong>"Pause on this site"</strong> or <strong>"Allow ads on {site}"</strong>.', {
              site: window.location.hostname,
            })}
          </li>
          <li>{t("Hit the button below.")}</li>
        </ol>
        <button className="btn btn-primary btn-lg" onClick={() => window.location.reload()} disabled={checking}>
          {checking ? t("Checking…") : t("I've turned it off - let me in!")}
        </button>
      </div>
    </div>
  );
}
